import type { AclModuleChild, AclModuleRow } from './types';

/**
 * Module with its child list already parsed from the stringified JSON
 * the backend stores under `childModule`. The legacy app does the same
 * client-side parsing — the API stores child modules as a JSON string
 * blob rather than a relational table.
 */
export interface AclModuleWithChildren extends AclModuleRow {
  children: AclModuleChild[];
}

export interface PermissionFlags {
  read?: boolean;
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
  view_permission?: boolean;
}

export type PermissionsMap = Record<string, PermissionFlags>;

/**
 * Parse the `childModule` JSON-string column into an array. The legacy
 * frontend sometimes saw the field as a real array (older API versions)
 * so handle both shapes.
 */
export function parseChildren(raw: unknown): AclModuleChild[] {
  if (Array.isArray(raw)) return raw as AclModuleChild[];
  if (typeof raw !== 'string' || raw.length === 0) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AclModuleChild[]) : [];
  } catch {
    return [];
  }
}

/** Display order of modules in the ACL matrix — mirrors the sidebar order. */
export const ACL_MODULE_ORDER: string[] = [
  'Manage Users',
  'Manage Dialers',
  'Manage Campaign',
  'TTS Studio',
  'Voice Library',
  'Transcription',
  'Number Action',
  'Voice Plans',
  'TTS Plans',
  'Manage CLI',
  'Manage API CLI',
  'Manage Api Key',
  'Manage Groups',
  'Manage Locations',
  'Manage Trunks',
  'Inbound',
  'API Docs',
];

/** Annotate every row with a parsed `children` array (defaulting to []). */
export function withParsedChildren(
  rows: AclModuleRow[],
): AclModuleWithChildren[] {
  const withChildren = rows.map((m) => ({
    ...m,
    children: parseChildren(m.childModule),
  }));
  return withChildren.sort((a, b) => {
    const ia = ACL_MODULE_ORDER.indexOf(a.mname);
    const ib = ACL_MODULE_ORDER.indexOf(b.mname);
    // Modules not in the list go to the end, in their original order.
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
}

/**
 * Default permissions per module — children-bearing parents only expose
 * read + view_permission (the children carry the granular CRUD bits).
 */
export function defaultPermissionsFor(
  modules: AclModuleWithChildren[],
): PermissionsMap {
  const out: PermissionsMap = {};
  for (const m of modules) {
    const hasChild = m.children.length > 0;
    out[m.mname] = hasChild
      ? { read: false, view_permission: false }
      : {
          read: false,
          add: false,
          edit: false,
          delete: false,
          view_permission: false,
        };
    for (const c of m.children) {
      out[c.moduleName] = {
        read: false,
        add: false,
        edit: false,
        delete: false,
        view_permission: false,
      };
    }
  }
  return out;
}

/**
 * Hydrate a permissions map from an existing ACL setting's moduleOperation
 * JSON blob. Mirrors the legacy buildExpandedModulesFromPermissions logic.
 */
export function hydratePermissions(
  modules: AclModuleWithChildren[],
  moduleOperation: string | undefined,
): { permissions: PermissionsMap; expanded: Record<string, boolean> } {
  const base = defaultPermissionsFor(modules);
  const expanded: Record<string, boolean> = {};
  if (!moduleOperation) return { permissions: base, expanded };

  let parsed: Record<string, unknown> = {};
  try {
    const j = JSON.parse(moduleOperation);
    if (j && typeof j === 'object') parsed = j as Record<string, unknown>;
  } catch {
    return { permissions: base, expanded };
  }

  for (const m of modules) {
    const node = parsed[m.mname];
    if (!node || typeof node !== 'object') continue;
    const obj = node as PermissionFlags & { children?: unknown };

    // Copy parent-level flags. For children-bearing parents the legacy
    // app only stores `read` + `view_permission`, but copy whatever's
    // present defensively.
    base[m.mname] = {
      read: Boolean(obj.read),
      add: Boolean(obj.add),
      edit: Boolean(obj.edit),
      delete: Boolean(obj.delete),
      view_permission: Boolean(obj.view_permission),
    };

    if (!Array.isArray(obj.children)) continue;
    for (const childObj of obj.children) {
      if (!childObj || typeof childObj !== 'object') continue;
      const [childName, childPerms] = Object.entries(childObj)[0] ?? [];
      if (!childName) continue;
      base[childName] = {
        ...base[childName],
        ...((childPerms as PermissionFlags) ?? {}),
      };
      // Expand the parent if any of its children carry an enabled flag.
      const anyEnabled = Object.values(childPerms ?? {}).some(
        (v) => v === true,
      );
      if (anyEnabled) expanded[m.mname] = true;
    }
  }

  return { permissions: base, expanded };
}

/**
 * Build the moduleOperation JSON-string from the form state — same wire
 * shape the legacy buildAclPayload produced, but parent rows are now
 * STRUCTURAL ONLY in the UI. So we derive parent-level `read` and
 * `view_permission` from the children:
 *
 *   - parent.view_permission = OR over child.view_permission (so the
 *     sidebar group surfaces whenever any child is reachable).
 *   - parent.read = OR over child.read (rare consumer, but kept for
 *     wire compatibility with the legacy reader).
 *
 * Leaf modules (no children) keep their five flags as set by the user.
 */
export function buildModuleOperation(
  modules: AclModuleWithChildren[],
  permissions: PermissionsMap,
): string {
  const out: Record<string, unknown> = {};
  for (const m of modules) {
    const hasChild = m.children.length > 0;
    if (!hasChild) {
      out[m.mname] = { ...(permissions[m.mname] ?? {}) };
      continue;
    }
    const anyChildView = m.children.some(
      (c) => permissions[c.moduleName]?.view_permission === true,
    );
    const anyChildRead = m.children.some(
      (c) => permissions[c.moduleName]?.read === true,
    );
    out[m.mname] = {
      read: anyChildRead,
      view_permission: anyChildView,
      children: m.children.map((c) => ({
        [c.moduleName]: permissions[c.moduleName] ?? {},
      })),
    };
  }
  return JSON.stringify(out);
}

/**
 * "Manage Locations" is special-cased to match the legacy app: instead
 * of a generic "Create Location" CRUD child row, the parent row itself
 * carries a multi-select of the real locations (from /api/obd/locations)
 * this role is scoped to.
 */
export const LOCATIONS_MODULE_NAME = 'Manage Locations';
export const USERS_MODULE_NAME = 'Manage Users';
export const NUMBER_ACTION_MODULE_NAME = 'Number Action';

export const MANAGE_USERS_OPTIONS = [
  { name: 'Setting 1', id: 'setting1' },
  { name: 'Setting 2', id: 'setting2' },
  { name: 'Setting 3', id: 'setting3' },
  { name: 'Setting 4', id: 'setting4' },
  { name: 'Submission', id: 'submission' },
];

export const NUMBER_ACTION_OPTIONS = [
  { name: 'Remove DND', id: 'remove_dnd' },
  { name: 'Remove Blacklist', id: 'remove_blacklist' },
  { name: 'Remove Whitelist', id: 'remove_whitelist' },
];

const LOCATIONS_HIDDEN_CHILD = 'Create Location';

/**
 * "Manage Dialers" gets the same treatment as "Manage Locations": the
 * parent row carries a multi-select of the real dialers (from
 * /api/obd/dialers/list) this role is scoped to, alongside its normal
 * CRUD children (Search Dialer / Create Dialer / Dialer List / Dialer
 * Usage — none of those are hidden, unlike Locations' "Create Location").
 */
export const DIALERS_MODULE_NAME = 'Manage Dialers';

/** Child rows to actually render as CRUD checkboxes — hides the ones
 *  represented by a dedicated widget instead (currently just
 *  "Create Location" under "Manage Locations"). */
export function visibleChildren(m: AclModuleWithChildren): AclModuleChild[] {
  if (m.mname !== LOCATIONS_MODULE_NAME) return m.children;
  return m.children.filter((c) => c.moduleName !== LOCATIONS_HIDDEN_CHILD);
}

/** moduleListingData wire shape: module name -> selected item ids. */
export type ModuleListingData = Record<string, number[] | string[]>;

export function parseModuleListingData(raw: string | undefined): ModuleListingData {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ModuleListingData) : {};
  } catch {
    return {};
  }
}

export function buildModuleListingData(data: ModuleListingData): string {
  return JSON.stringify(data);
}